/**
 * CardElement - Iframe-based payment element
 * 
 * Mounts an iframe with the payment checkout form
 */

import { resolvePaymentElementOptions } from './element-options';
import { SDK } from './sdk';

import type {
  CSSProperties,
  Locale,
  PaymentElementAppearance,
  PaymentElementOptions,
  PaymentResult,
  SaveCardResult,
} from './types';
import { PaymentResultStatus } from './types';
import { detectBrowserLocale } from './utils';

export interface CardElementOptions {
  /** Container element or CSS selector where the iframe will be mounted */
  container: string | HTMLElement;
  /** Payment ID for a payment transaction. Mutually exclusive with setupId. */
  paymentId?: string;
  /** CustomerPaymentMethodRequest ID for a save-card session. Mutually exclusive with paymentId. */
  setupId?: string;
  /** Callback when the operation completes (payment or card setup) */
  onComplete?: (result: PaymentResult | SaveCardResult) => void;
  /** Callback when the operation fails */
  onError?: (error: any) => void;
  /** Callback when user closes the element */
  onClose?: () => void;
  /** Visual theme for the checkout form. Takes priority over `style` when set. */
  appearance?: PaymentElementAppearance;
  /** @deprecated Use `appearance`. Still applied when `appearance` is omitted. */
  style?: CSSProperties;
  /** Layout, width, button label, placeholders, and success UI settings. */
  options?: PaymentElementOptions;
  /** @deprecated Use `options.buttonText`. */
  buttonText?: string;
  /** @deprecated Use `options.cardForm.placeholders`. */
  placeholders?: PaymentElementOptions['placeholders'];
  /** @deprecated Use `options.showDefaultSuccessUI`. */
  showDefaultSuccessUI?: boolean;
}

interface InternalSDKConfig {
  publicKey: string;
  iframeUrl: string;
  timeout: number;
  debug: boolean;
  locale?: Locale;
}

export class CardElement {
  private sdk: SDK;
  private container: HTMLElement;
  private mounted: boolean = false;

  constructor(
    config: InternalSDKConfig,
    options: CardElementOptions
  ) {
    if (!options.paymentId && !options.setupId) {
      throw new Error('CardElement: paymentId or setupId is required');
    }
    if (options.paymentId && options.setupId) {
      throw new Error('CardElement: paymentId and setupId are mutually exclusive');
    }

    let containerElement: HTMLElement | null;
    if (typeof options.container === 'string') {
      containerElement = document.querySelector(options.container);
      if (!containerElement) {
        throw new Error(`Container not found: ${options.container}`);
      }
    } else {
      containerElement = options.container;
    }
    this.container = containerElement;

    const isSaveCardMode = Boolean(options.setupId);
    const elementOptions = resolvePaymentElementOptions(options);

    this.sdk = new SDK({
      publicKey: config.publicKey,
      container: this.container,
      locale: config.locale ?? detectBrowserLocale(),
      config: {
        ...(options.paymentId && { paymentId: options.paymentId }),
        ...(options.setupId && { setupId: options.setupId }),
        ...(options.appearance && { appearance: options.appearance }),
        ...(options.style && { style: options.style }),
        options: elementOptions,
        ...(elementOptions.buttonText && { buttonText: elementOptions.buttonText }),
        ...(elementOptions.placeholders && {
          placeholders: elementOptions.placeholders,
        }),
        ...(elementOptions.cardForm && { cardForm: elementOptions.cardForm }),
        ...(elementOptions.showDefaultSuccessUI !== undefined && {
          showDefaultSuccessUI: elementOptions.showDefaultSuccessUI,
        }),
      },
      onSuccess: (data) => {
        if (options.onComplete) {
          if (isSaveCardMode) {
            options.onComplete({
              status: PaymentResultStatus.SUCCESS,
              setupId: options.setupId as string,
            } satisfies SaveCardResult);
          } else {
            options.onComplete({
              status: PaymentResultStatus.SUCCESS,
              paymentId: options.paymentId as string,
            } satisfies PaymentResult);
          }
        }
        this.sdk.switchToSuccessState(data);
      },
      onError: (error) => {
        if (options.onError) {
          options.onError(error);
        } else if (options.onComplete) {
          if (isSaveCardMode) {
            options.onComplete({
              status: PaymentResultStatus.FAILED,
              setupId: options.setupId as string,
              error,
            } satisfies SaveCardResult);
          } else {
            options.onComplete({
              status: PaymentResultStatus.FAILED,
              paymentId: options.paymentId as string,
              error,
            } satisfies PaymentResult);
          }
        }
      },
      onClose: () => {
        if (options.onClose) {
          options.onClose();
        }
      },
    });
  }

  /**
   * Mount the CardElement to the DOM
   * This will create and display the iframe
   */
  mount(): void {
    if (this.mounted) {
      throw new Error('CardElement is already mounted');
    }

    this.sdk.init();
    this.mounted = true;
  }

  /**
   * Destroy the CardElement and cleanup
   */
  destroy(): void {
    if (this.mounted) {
      this.sdk.destroy();
      this.mounted = false;
    }
  }
}

